import React, { useState, useEffect } from 'react';
import { Container, Typography, Box } from '@mui/material';
import RiskScale from './RiskScale';
import IntroLoader from './IntroLoader';
import './IntroLoader.css';
import { theme, Section, CardSection } from './theme';
import { useRiskCalculation } from './hooks/useRiskCalculation';
import Logo2 from './assets/logo2.svg';

function App() {
  const [showLoader, setShowLoader] = useState(true);

  // Disable scrolling when loader is showing
  useEffect(() => {
    if (showLoader) {
      // Disable scrolling
      document.body.style.overflow = 'hidden';
      // Scroll to top
      window.scrollTo(0, 0);
    } else {
      // Re-enable scrolling
      document.body.style.overflow = 'unset';
      // Ensure we're at the top when loader finishes
      window.scrollTo(0, 0);
    }

    // Cleanup function to restore scrolling if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLoader]);
  
  // Get calculated risk data from URL parameters
  const riskData = useRiskCalculation();
  const { isBunoReferral, doctorName } = riskData;

  // Helper function to get risk category
  const getRiskCategory = (score) => {
    if (score <= 2) return 'low';
    if (score <= 4) return 'intermediate';
    return 'high';
  };

  const riskCategory = getRiskCategory(riskData.level);

  // Helper function to get risk level text and color
  const getRiskInfo = (category) => {
    switch (category) {
      case 'low':
        return {
          text: 'Riesgo Bajo',
          color: '#38A169',
          bgColor: '#E8F5E8'
        };
      case 'intermediate':
        return {
          text: 'Riesgo Moderado',
          color: '#DD6B20',
          bgColor: '#FFF3E8'
        };
      case 'high':
        return {
          text: 'Riesgo Alto',
          color: '#E53E3E',
          bgColor: '#FFE8E8'
        };
      default:
        return {
          text: 'Riesgo Bajo',
          color: '#38A169',
          bgColor: '#E8F5E8'
        };
    }
  };

  const riskInfo = getRiskInfo(riskCategory);

  return (
    <>
      {/* Loader overlay on top of app content */}
      {showLoader && (
        <IntroLoader onFinish={() => setShowLoader(false)} />
      )}
      <Container sx={{ backgroundColor: theme.palette.bunoColors.eggshell, height: "100vh", paddingLeft: 0, paddingRight: 0, paddingBottom: "40px", paddingTop: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: "1.5rem" }}>
        <Section id="risk-assessment" style={{
          borderRadius: 0,
          paddingBottom: 0, paddingTop: theme.spacing(2),
        }}>
          <img src={Logo2} alt="Buno Logo" style={{width: '100px', height: 'auto', paddingBottom: theme.spacing(2)}} />
          
          <RiskScale 
            level={riskData.level} 
            apneaLikelihood={riskData.apneaLikelihood} 
          />
        </Section>

        {/* Risk Assessment Results */}
        
        <CardSection
          style={{
            borderRadius: 20,
            backgroundColor: 'white',
            padding: theme.spacing(3),
            margin: theme.spacing(1),
            maxWidth: '800px',
          }}
        >
          {/* Score Display */}
          <Typography variant="h3" sx={{ 
            fontWeight: 600, 
            marginBottom: 2,
            color: theme.palette.bunoColors.spaceBlue
          }}>
            Riesgo de apnea: {riskData.level} / 8
          </Typography>

          {/* Risk Category Explanation */}
          <Box sx={{
            backgroundColor: riskInfo.bgColor,
            padding: 2,
            borderRadius: 2,
            marginBottom: 3
          }}>
            {riskCategory === 'low' && (
              <Typography variant="body2">
                Esta puntuación indica un <strong style={{color: riskInfo.color}}>riesgo bajo</strong> de padecer apnea obstructiva del sueño.<br/><br/>Sin embargo, si experimentas síntomas como ronquidos intensos, pausas respiratorias durante el sueño, o somnolencia diurna excesiva, te recomendamos consultar con un especialista en medicina del sueño.
              </Typography>
            )}

            {riskCategory === 'intermediate' && (
              <Typography variant="body2">
                Esta puntuación indica un <strong style={{color: riskInfo.color}}>riesgo moderado</strong> de padecer apnea obstructiva del sueño.<br/><br/>Es recomendable considerar realizarse un estudio del sueño para una evaluación más completa.
              </Typography>
            )}

            {riskCategory === 'high' && (
              <Typography variant="body2">
                Esta puntuación indica un <strong style={{color: riskInfo.color}}>riesgo alto</strong> de padecer apnea obstructiva del sueño.<br/><br/>Es recomendable considerar realizarse un estudio del sueño para una evaluación más completa.
              </Typography>
            )}
          </Box>

          {/* Recommendation Box */}
          <Box sx={{
            backgroundColor: 'white',
            border: `2px solid ${riskInfo.color}`,
            padding: 2,
            borderRadius: 2
          }}>
            <Typography variant="h3" sx={{ 
              fontWeight: 600, 
              marginBottom: 1,
              color: theme.palette.bunoColors.spaceBlue
            }}>
              Recomendación:
            </Typography>
            {riskCategory === 'low' && (
              <>
                <Typography variant="body2">
                  {isBunoReferral
                    ? 'Mantener hábitos de sueño saludables y consultar si síntomas están presentes.'
                    : `Mantener hábitos de sueño saludables y consultar ${doctorName} si síntomas están presentes.`}
                </Typography>
                {isBunoReferral && (
                  <Typography variant="body2" sx={{ marginTop: 2 }}>
                    ¿Te gustaría que te conectemos con un médico que pueda realizarte un estudio del sueño en tu zona? <a href="https://bunomx.typeform.com/to/UxO2Ioo0" style={{ color: riskInfo.color, fontWeight: 600 }}>Déjanos tus datos aquí</a>
                  </Typography>
                )}
              </>
            )}

            {riskCategory === 'intermediate' && (
              <>
                <Typography variant="body2">
                  {isBunoReferral
                    ? 'Considerar realizar un estudio del sueño para descartar apnea obstructiva del sueño.'
                    : `Mostrarle tus resultados ${doctorName} para entender mejor tu situación.`}
                </Typography>
                {isBunoReferral && (
                  <Typography variant="body2" sx={{ marginTop: 2 }}>
                    ¿Te gustaría que te conectemos con un médico que pueda realizarte un estudio del sueño en tu zona? <a href="https://bunomx.typeform.com/to/UxO2Ioo0" style={{ color: riskInfo.color, fontWeight: 600 }}>Déjanos tus datos aquí</a>
                  </Typography>
                )}
              </>
            )}

            {riskCategory === 'high' && (
              <>
                <Typography variant="body2">
                  {isBunoReferral
                    ? 'Realizar un estudio del sueño lo antes posible para descartar apnea obstructiva del sueño.'
                    : `Mostrarle tus resultados ${doctorName} para entender mejor tu situación.`}
                </Typography>
                {isBunoReferral && (
                  <Typography variant="body2" sx={{ marginTop: 2 }}>
                    ¿Te gustaría que te conectemos con un médico que pueda realizarte un estudio del sueño en tu zona? <a href="https://bunomx.typeform.com/to/UxO2Ioo0" style={{ color: riskInfo.color, fontWeight: 600 }}>Déjanos tus datos aquí</a>
                  </Typography>
                )}
              </>
            )}
          </Box>
        </CardSection>
      </Container>
    </>
  );
}

export default App;